import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Container,
  Button,
  Grid,
  Stack,
  TextField,
  IconButton
} from '@mui/material';
import { 
  Facebook,
  Twitter,
  Instagram,
  YouTube,
  LinkedIn,
  Email,
  Phone,
  LocationOn
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import NotFound from './NotFound';

const SingleNewsPage = () => {  // Changed from Allpolnews to SingleNewsPage for clarity
  const { id } = useParams();
  const [newsItem, setNewsItem] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  
  const categories = [
    "All",
    "Transport",
    "Politics",
    "Business",
    "Sports",
    "Technology",
  ];

  React.useEffect(() => {
    const fetchNewsItem = async () => {
      try {
        const response = await fetch(`https://ethioinsider-backend.onrender.com/api/articles/${id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch news item');
        }
        const result = await response.json();
        if (result.data) {
          setNewsItem(result.data);
        } else {
          throw new Error('Unexpected API response format');
        }
      } catch (err) {
        setError(err.message);
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsItem();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body1" color="error" sx={{ textAlign: "center", py: 4, mt: 10 }}>
        Error loading news: {error}
      </Typography>
    );
  }

  if (!newsItem) {
    return <NotFound />;
  }

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4, mt: 10 }}>
        <Button 
          component={Link} 
          to="/politics-news" 
          variant="outlined" 
          sx={{ mb: 4 }}
        >
          Back to Politics News
        </Button>
        
        <Typography variant="h3" component="h1" gutterBottom>
          {newsItem.title}
        </Typography>
        
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Published on: {new Date(newsItem.createdAt).toLocaleDateString()}
        </Typography>
        
        <Card sx={{ mb: 4 }}>
          <CardMedia
            component="img"
            height="400"
            image={newsItem.imageUrl || "https://via.placeholder.com/800x450?text=News"}
            alt={newsItem.title}
            sx={{ objectFit: "cover" }}
            onError={(e) => {
              e.target.src = "https://via.placeholder.com/800x450?text=News";
            }}
          />
        </Card>
        
        <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
          {newsItem.content || "No content available"}
        </Typography>
      </Container>
      
      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          px: 2,
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Quick Links */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Quick Links
              </Typography>
                             <Stack spacing={1}>
                 {[
                   { name: "Home", path: "/" },
                   { name: "About Us", path: "/about" },
                   { name: "Contact", path: "/contact" }
                 ].map((item) => (
                   <motion.div whileHover={{ x: 5 }} key={item.name}>
                     <Link
                       to={item.path}
                       style={{
                         display: "block",
                         color: "inherit",
                         textDecoration: "none",
                       }}
                       sx={{
                         color: "text.secondary",
                         "&:hover": { color: "primary.main" },
                       }}
                     >
                       {item.name}
                     </Link>
                   </motion.div>
                 ))}
              </Stack>
            </Grid>

            {/* Categories */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Categories
              </Typography>
              <Stack spacing={1}>
                {["Politics", "Business", "Sports"].map((category) => (
                  <motion.div whileHover={{ x: 5 }} key={category}>
                    <Link
                      to={`/${category.toLowerCase()}-news`}
                      style={{
                        display: "block",
                        color: "inherit",
                        textDecoration: "none",
                      }}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {category}
                    </Link>
                  </motion.div>
                ))}
              </Stack>
            </Grid>

            {/* Contact Info */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Contact Us
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LocationOn color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    123 News Street, Addis Ababa, Ethiopia
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Phone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">+251 123 456 789</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Email color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">info@ethioreview.com</Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Social Media & Newsletter */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Follow Us
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Facebook />, color: "#3b5998", name: "Facebook" },
                  { icon: <Twitter />, color: "#1DA1F2", name: "Twitter" },
                  { icon: <Instagram />, color: "#E1306C", name: "Instagram" },
                  { icon: <YouTube />, color: "#FF0000", name: "YouTube" },
                  { icon: <LinkedIn />, color: "#0077B5", name: "LinkedIn" },
                ].map((social) => (
                  <motion.div whileHover={{ scale: 1.1 }} key={social.name}>
                    <IconButton
                      href="#"
                      target="_blank"
                      rel="noopener"
                      sx={{
                        backgroundColor: `${social.color}10`,
                        color: social.color,
                        "&:hover": { backgroundColor: `${social.color}20` },
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  </motion.div>
                ))}
              </Stack>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Newsletter
              </Typography>
              <Box component="form" sx={{ display: "flex" }}>
                <TextField
                  size="small"
                  placeholder="Your email"
                  variant="outlined"
                  fullWidth
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Copyright & Legal */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} EthioReview. All rights reserved.
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
              {[
                { name: "Privacy Policy", path: "/privacy" },
                { name: "Terms of Service", path: "/terms" },
                { name: "Cookie Policy", path: "/cookies" }
              ].map((item) => (
                <Typography
                  key={item.name}
                  component={Link}
                  to={item.path}
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textDecoration: "none",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {item.name}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default SingleNewsPage;  // Changed to match component name